import { useState, useEffect, type ReactElement, type ReactNode } from 'react';

interface AnimatedWidgetProps {
  children: ReactNode;
  /** Extra delay in ms before this widget starts its entrance */
  delay?: number;
}

/**
 * Wraps any widget with a staggered fade-in + slide-up entrance on mount.
 * Uses the `.widget-enter` / `.widget-enter.visible` CSS classes from
 * `styles/animations.css`.
 *
 * All widgets become visible shortly after mount — the `delay` prop creates
 * a staggered cascade without requiring IntersectionObserver. This avoids
 * the "invisible gap" problem where opacity-0 widgets take up layout space
 * but aren't visible until the user scrolls far enough to trigger an
 * observer.
 */
export function AnimatedWidget({ children, delay = 0 }: AnimatedWidgetProps): ReactElement {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Respect reduced-motion preference
    if (
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      setVisible(true);
      return;
    }

    // Use requestAnimationFrame to ensure the element is painted at opacity:0
    // first, then trigger the CSS transition on the next frame.
    const raf = requestAnimationFrame(() => {
      setVisible(true);
    });
    return (): void => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      className={`widget-enter${visible ? ' visible' : ''}`}
      style={delay > 0 ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  );
}
